import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap-5';

import { createActivity } from '../../../helpers/prospect_api';
import { ACTIVITY_TYPE_CALL, TASK_PRIORITY_MEDIUM } from '../../../constants';
import TaskForm from './task_form';
import type { Activity, ConnectedRecord } from '../../../types';

const DEFAULT_NEW_ACTIVITY: Activity = {
  name: '',
  priority: TASK_PRIORITY_MEDIUM,
  description: '',
  type: ACTIVITY_TYPE_CALL,
  connectedRecordsAttributes: Array<ConnectedRecord>(),
  isTask: true,
  callListName: '',
  callResult: '',
  createdAt: '',
  emailSubject: '',
  location: ''
} as Activity; // we need to leave ID empty or we get a record not found, and this causes fewer problems then
// making ID optional on the Activity type

type NewTaskModalProps = {
  autoAddConnectedRecords?: ConnectedRecord[];
  callListId?: number;
  initialConnectedRecords?: ConnectedRecord[];
  show: boolean;
  title?: string;
  onAdd: (activity: any) => void;
  onHide: () => void;
}

export default function NewTaskModal({
  autoAddConnectedRecords,
  callListId,
  initialConnectedRecords,
  show,
  title = 'New Task',
  onAdd,
  onHide
}: NewTaskModalProps) {
  const [activity, setActivity] = useState(DEFAULT_NEW_ACTIVITY);
  const [saving, setSaving] = useState(false);
  const isValid = activity.name !== '';

  useEffect(() => {
    if (!show) setActivity(DEFAULT_NEW_ACTIVITY);
  }, [show]);

  useEffect(() => {
    setActivity({ ...DEFAULT_NEW_ACTIVITY, ...{ connectedRecordsAttributes: initialConnectedRecords || [] } });
  }, [initialConnectedRecords]);

  const handleSave = () => {
    setSaving(true);

    const activityParams = { ...activity };
    activityParams.connectedRecordsAttributes = [
      ...activity.connectedRecordsAttributes,
      ...(autoAddConnectedRecords || [])
    ];

    createActivity(activityParams, callListId)
      .then((activity) => {
        onAdd(activity);
        setActivity(DEFAULT_NEW_ACTIVITY);
      })
      .catch(() => {
        // errors
      })
      .finally(() => {
        setSaving(false);
        onHide();
      });
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header className="flex-row" closeButton>
        <Modal.Title as="h5">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-3">
        <TaskForm activity={activity} onChange={setActivity} />
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={saving || !isValid} variant="primary" onClick={handleSave}>
          {saving && <Spinner animation="border" size="sm" />}
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
